import * as THREE from 'three'
import type { Wall } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const WALL_TEXTURES: Record<string, { opacity: number; roughness: number; metalness: number }> = {
  solid: { opacity: 1.0, roughness: 0.8, metalness: 0.1 },
  glass: { opacity: 0.4, roughness: 0.1, metalness: 0.3 },
  fabric: { opacity: 1.0, roughness: 1.0, metalness: 0.0 },
}

const HANDLE_RADIUS = 0.15
const HANDLE_COLOR_LEFT = 0x4ade80
const HANDLE_COLOR_RIGHT = 0x60a5fa
const HANDLE_COLOR_ROTATE = 0xe94560

export class WallRenderer {
  manualWalls: THREE.Group
  ifcWalls: THREE.Group
  private handles: THREE.Group = new THREE.Group()
  private selectedWallId: string | null = null
  private manualWallMeshes: THREE.Mesh[] = []

  constructor() {
    this.manualWalls = new THREE.Group()
    this.manualWalls.name = 'manual-walls'
    this.ifcWalls = new THREE.Group()
    this.ifcWalls.name = 'ifc-walls'
    this.handles.name = 'wall-handles'
  }

  setManualWalls(walls: Wall[], floorGroups: Map<number, THREE.Group>) {
    this.clearManualWalls()
    for (const wall of walls) {
      if (wall.porteur) {
        // Porteur wall: one mesh per floor
        for (const [, floorGroup] of floorGroups) {
          const mesh = this.createManualWall(wall)
          floorGroup.add(mesh)
          this.manualWallMeshes.push(mesh)
        }
      } else {
        const mesh = this.createManualWall(wall)
        const floorGroup = floorGroups.get(wall.floor)
        if (floorGroup) {
          floorGroup.add(mesh)
        } else {
          this.manualWalls.add(mesh)
        }
        this.manualWallMeshes.push(mesh)
      }
    }
  }

  private createManualWall(wall: Wall): THREE.Mesh {
    const geometry = new THREE.BoxGeometry(wall.width, wall.height, wall.depth)
    const texProps = WALL_TEXTURES[wall.texture ?? 'solid'] ?? WALL_TEXTURES.solid!
    const material = new THREE.MeshStandardMaterial({
      color: wall.color,
      transparent: texProps.opacity < 1.0,
      opacity: texProps.opacity,
      roughness: texProps.roughness,
      metalness: texProps.metalness,
      depthWrite: true,
    })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.set(wall.x, wall.height / 2, wall.z)
    mesh.rotation.y = wall.rotation
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.name = `wall-${wall.id}`
    mesh.userData = { wallId: wall.id, type: 'wall' }
    return mesh
  }

  async loadIFCWalls(geometryUrls: Record<string, string>, floorGroups: Map<number, THREE.Group>, token?: string) {
    this.clearIFCWalls()
    for (const [floorStr, url] of Object.entries(geometryUrls)) {
      try {
        const { positions, normals, indices } = await this.loadIFCGeometry(url, token)
        const mesh = this.createIFCWall(positions, normals, indices)
        const floorIndex = parseInt(floorStr, 10)
        const floorGroup = floorGroups.get(floorIndex)
        if (floorGroup) {
          floorGroup.add(mesh)
        } else {
          this.ifcWalls.add(mesh)
        }
      } catch (e) {
        console.warn(`Failed to load IFC geometry for floor ${floorStr}:`, e)
      }
    }
  }

  private createIFCWall(positions: Float32Array, normals: Float32Array, indices: Uint16Array): THREE.Mesh {
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
    geometry.setIndex(new THREE.BufferAttribute(indices, 1))
    const material = new THREE.MeshPhongMaterial({
      color: 0xcccccc,
      side: THREE.DoubleSide,
    })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.name = 'ifc-wall'
    mesh.userData = { type: 'ifc-wall' }
    return mesh
  }

  private async loadIFCGeometry(url: string, token?: string): Promise<{
    positions: Float32Array
    normals: Float32Array
    indices: Uint16Array
  }> {
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const response = await fetch(fullUrl, { headers })
    const buffer = await response.arrayBuffer()
    const view = new DataView(buffer)
    let offset = 0

    const vertexCount = view.getUint32(offset, true)
    if (vertexCount > 500000) {
      console.error('[WallRenderer] Invalid vertex count:', vertexCount)
      return { positions: new Float32Array(0), normals: new Float32Array(0), indices: new Uint16Array(0) }
    }
    offset += 4
    const positions = new Float32Array(buffer, offset, vertexCount * 3)
    offset += vertexCount * 3 * 4
    const normals = new Float32Array(buffer, offset, vertexCount * 3)
    offset += vertexCount * 3 * 4
    const indexCount = view.getUint32(offset, true)
    offset += 4
    const indices = new Uint16Array(buffer, offset, indexCount)

    return { positions, normals, indices }
  }

  showHandles(wallId: string, wall: Wall, parentGroup: THREE.Object3D) {
    this.clearHandles()
    this.selectedWallId = wallId

    const halfWidth = wall.width / 2
    const handleY = wall.height / 2

    // Left handle - controls width from left side
    const leftHandle = this.createHandle(HANDLE_COLOR_LEFT, 'left')
    leftHandle.position.set(-halfWidth, handleY, 0)

    // Right handle - controls width from right side
    const rightHandle = this.createHandle(HANDLE_COLOR_RIGHT, 'right')
    rightHandle.position.set(halfWidth, handleY, 0)

    // Rotate handle - above the wall center
    const rotateHandle = this.createHandle(HANDLE_COLOR_ROTATE, 'rotate')
    rotateHandle.position.set(0, wall.height + 0.5, 0)

    this.handles.position.set(wall.x, 0, wall.z)
    this.handles.rotation.y = wall.rotation

    this.handles.add(leftHandle)
    this.handles.add(rightHandle)
    this.handles.add(rotateHandle)

    parentGroup.add(this.handles)
  }

  hideHandles() {
    this.clearHandles()
    this.selectedWallId = null
  }

  getSelectedWallId(): string | null {
    return this.selectedWallId
  }

  getHandlesGroup(): THREE.Group {
    return this.handles
  }

  private createHandle(color: number, handleType: 'left' | 'right' | 'rotate'): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(HANDLE_RADIUS, 16, 16)
    const material = new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.3 })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.userData = { isHandle: true, handleType }
    mesh.name = `handle-${handleType}`
    return mesh
  }

  private clearHandles() {
    while (this.handles.children.length > 0) {
      const child = this.handles.children[0]!
      this.handles.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose())
        } else {
          child.material.dispose()
        }
      }
    }
    if (this.handles.parent) {
      this.handles.parent.remove(this.handles)
    }
  }

  setManualWallsVisible(visible: boolean) {
    this.manualWalls.visible = visible
  }

  setIFCRotation(rotation: number) {
    this.ifcWalls.rotation.y = rotation * Math.PI / 180
  }

  setIFCWallsVisible(visible: boolean) {
    this.ifcWalls.visible = visible
  }

  private clearManualWalls() {
    for (const mesh of this.manualWallMeshes) {
      if (mesh.parent) {
        mesh.parent.remove(mesh)
      }
      mesh.geometry.dispose()
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m) => m.dispose())
      } else {
        mesh.material.dispose()
      }
    }
    this.manualWallMeshes = []
    this.disposeGroup(this.manualWalls)
  }

  private clearIFCWalls() {
    this.disposeGroup(this.ifcWalls)
  }

  private disposeGroup(group: THREE.Group) {
    while (group.children.length > 0) {
      const child = group.children[0]!
      group.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose())
        } else {
          child.material.dispose()
        }
      }
    }
  }

  dispose() {
    this.clearHandles()
    this.clearManualWalls()
    this.clearIFCWalls()
  }
}
